// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import useTenants from "../../hooks/useTenants";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import { useRoutes } from "../../hooks/useRoutes";
import { hasSuperAdminAccess } from "../../config/roleConfig";
import { TIME_RANGES, getDefaultTimeRange } from "../../utils/dateRanges";
import { getAdminTenantConfig } from "../../utils/tenantConfig";
import useTenantBranding from "../../hooks/useTenantBranding";
import { bookingService } from "../../services/bookingService";
import llmUsageService from "../../services/llmUsageService";

const REFRESH_INTERVAL_MS = 30_000;

// Live Booking Trends time windows (hourly granularity), in hours.
const TREND_HOURS = { "1H": 1, "4H": 4, "24H": 24 };

const formatEur = (n) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const formatChange = (trend) => {
  if (!trend) return null;
  const pct = Number(trend.percentage ?? 0).toFixed(1);
  const period = trend.vs_period || "vs previous";
  const sign = trend.direction === "down" ? "-" : "+";
  return `${sign}${pct}% ${period}`;
};

// Relative "x min ago" label from an ISO/date value (empty when unparseable).
const timeAgo = (value) => {
  if (!value) return "";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  return `${Math.round(hrs / 24)} d ago`;
};

// Tab + icon for an activity, from the backend `category`
// (bookings | payments | system), with an event_type fallback for older
// responses that don't send a category yet.
const TAB_BY_CATEGORY = {
  bookings: "Bookings",
  payments: "Payments",
  system: "System",
};
const TAB_ICON = { Bookings: "📅", Payments: "💰", System: "🤖" };

const inferTab = (eventType = "") => {
  const e = String(eventType).toLowerCase();
  if (e.startsWith("booking")) return "Bookings";
  if (e.startsWith("payment")) return "Payments";
  return "System";
};

// Map the unified /metrics/bookings/recent-activity feed into the
// {category, icon, title, details, ts} shape the list renders, newest-first.
const buildActivities = (activityResp) => {
  const items =
    activityResp?.data ?? (Array.isArray(activityResp) ? activityResp : []);

  return items
    .map((it) => {
      const tab =
        TAB_BY_CATEGORY[String(it.category || "").toLowerCase()] ||
        inferTab(it.event_type);
      const parts = [];
      if (it.title) parts.push(it.title);
      if (it.booking_number) parts.push(`#${it.booking_number}`);
      if (it.details) parts.push(it.details);
      return {
        category: tab,
        icon: TAB_ICON[tab] || "•",
        title: it.event_type || "Activity",
        details: parts.join(" • "),
        ts: it.timestamp,
      };
    })
    .sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0))
    .slice(0, 12);
};

const AdminDashboard = () => {
  const { userRoles } = useRoutes();
  const isSuperAdmin = hasSuperAdminAccess(userRoles || []);

  const {
    tenantOptions,
    selectedTenant,
    setSelectedTenant,
    loading: tenantsLoading,
  } = useTenants(getAdminTenantConfig(isSuperAdmin));

  // Tenant-specific branding (company name, etc.)
  const { companyName } = useTenantBranding();

  const [selectedRange, setSelectedRange] = useState(getDefaultTimeRange());
  const [lastUpdated, setLastUpdated] = useState(
    new Date().toLocaleTimeString("de-DE"),
  );

  const headerInfo = useMemo(
    () => ({
      subtitle: `Live monitoring • Auto-refresh: 30s • Last updated: ${lastUpdated}`,
      status: "All Systems Operational",
    }),
    [lastUpdated],
  );

  const [bookingSummary, setBookingSummary] = useState(null);
  const [performanceToday, setPerformanceToday] = useState(null);
  const [bookingsData, setBookingsData] = useState([]);
  const [activities, setActivities] = useState([]);
  const [activityFilter, setActivityFilter] = useState("All");
  const [trendRange, setTrendRange] = useState("24H");

  const tenantId =
    isSuperAdmin && selectedTenant && selectedTenant !== "all"
      ? selectedTenant
      : null;

  useEffect(() => {
    let cancelled = false;

    const fetchDashboard = async () => {
      const [summary, perfToday, trends, activity] = await Promise.all([
        bookingService.getBookingSummary(true, "1d").catch(() => null),
        llmUsageService.getPerformanceToday(1, tenantId).catch(() => null),
        // Fetch the full hourly series; the 1H/4H/24H window is applied
        // client-side (the endpoint only accepts date-only ranges).
        bookingService.getBookingTrends("hourly").catch(() => null),
        // Unified feed: bookings + payments + system (incl. LLM executions).
        bookingService.getRecentActivity(12, tenantId).catch(() => null),
      ]);
      if (cancelled) return;
      setBookingSummary(summary);
      setPerformanceToday(perfToday);
      setBookingsData(
        (trends?.data || []).map((item) => ({
          time: item.date || item.period || item.label || "",
          bookings: Number(item.total_bookings ?? item.bookings ?? 0),
          revenue: Number(item.total_revenue ?? item.revenue ?? 0),
        })),
      );
      setActivities(buildActivities(activity));
      setLastUpdated(new Date().toLocaleTimeString("de-DE"));
    };

    fetchDashboard();
    const id = setInterval(fetchDashboard, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [tenantId]);

  const metrics = useMemo(
    () => [
      {
        label: "Active Bookings Today",
        icon: "📅",
        value: (bookingSummary?.total_bookings ?? 0).toLocaleString("de-DE"),
        change:
          formatChange(bookingSummary?.total_bookings_trend) ||
          "No comparison data",
        trend: bookingSummary?.total_bookings_trend?.direction || "up",
      },
      {
        label: "Revenue Today",
        icon: "💰",
        value: formatEur(bookingSummary?.total_revenue),
        change:
          formatChange(bookingSummary?.total_revenue_trend) ||
          "No comparison data",
        trend: bookingSummary?.total_revenue_trend?.direction || "up",
      },
      {
        // TODO: no backend endpoint for overall system response time; showing
        // LLM avg response time as a proxy. Replace when /system/metrics ships.
        label: "Avg LLM Response Time",
        icon: "⚡",
        value: `${Math.round(performanceToday?.avg_response_time_ms?.today ?? 0)}ms`,
        change:
          performanceToday?.avg_response_time_ms?.change_text ||
          "No comparison data",
        trend:
          (performanceToday?.avg_response_time_ms?.change_pct ?? 0) > 0
            ? "up"
            : "down",
      },
      {
        label: "LLM Calls Today",
        icon: "🤖",
        value: (performanceToday?.total_requests?.today ?? 0).toLocaleString(
          "de-DE",
        ),
        change:
          performanceToday?.success_rate?.today != null
            ? `Success: ${Number(performanceToday.success_rate.today).toFixed(1)}%`
            : "No data",
        trend: "up",
      },
    ],
    [bookingSummary, performanceToday],
  );

  const visibleActivities =
    activityFilter === "All"
      ? activities
      : activities.filter((a) => a.category === activityFilter);

  // TODO: no backend endpoint for system health metrics — these are static
  // placeholders until /system/health (or equivalent) is available.
  const health = [
    { score: 98, label: "API Uptime", value: "98.7%", intent: "excellent" },
    { score: 245, label: "Response Time", value: "245ms", intent: "good" },
    { score: 94, label: "Cache Hit Rate", value: "94.2%", intent: "excellent" },
    { score: 23, label: "CPU Usage", value: "23%", intent: "warning" },
    { score: 342, label: "Memory Usage", value: "342MB", intent: "good" },
  ];

  // Apply the selected 1H/4H/24H window to the hourly series (last N points).
  const trendData = useMemo(
    () => bookingsData.slice(-(TREND_HOURS[trendRange] || bookingsData.length)),
    [bookingsData, trendRange],
  );

  const llmPieData = useMemo(() => {
    const success = Number(performanceToday?.success_rate?.today ?? 0);
    const failure = Math.max(0, 100 - success);
    return [
      { name: "Success", value: Number(success.toFixed(1)) },
      { name: "Failure", value: Number(failure.toFixed(1)) },
    ];
  }, [performanceToday]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <GlobalAdminHeader
        title={
          companyName
            ? `${companyName} – Operations Dashboard`
            : "Real-time Operations Dashboard"
        }
        subtitle={headerInfo.subtitle}
        status={headerInfo.status}
        statusColor="green"
        tenantOptions={isSuperAdmin ? tenantOptions : []}
        selectedTenant={isSuperAdmin ? selectedTenant : null}
        onTenantChange={isSuperAdmin ? setSelectedTenant : undefined}
        tenantsLoading={isSuperAdmin ? tenantsLoading : false}
        ranges={TIME_RANGES}
        selectedRange={selectedRange}
        onRangeChange={setSelectedRange}
        buttons={[
          {
            text: "Export Live Report",
            // TODO: wire up export — no backend endpoint yet
            onClick: () => {},
            className:
              "bg-primary-color text-white px-4 py-2 rounded-md hover:bg-primary-color-dark text-sm",
          },
        ]}
      />

      {/* Content */}
      <div className="mx-auto px-4 lg:px-4 py-4 space-y-6">
        {/* Real-time Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {metrics.map((m) => (
            <div
              key={m.label}
              className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 p-4 border border-slate-200"
            >
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  {m.label}
                </div>
                <div className="text-lg" aria-hidden>
                  {m.icon}
                </div>
              </div>
              <div className="mt-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                {m.value}
              </div>
              <div
                className={`mt-1 text-sm ${m.trend === "down" ? "text-green-600 dark:text-green-400" : "text-green-600 dark:text-green-400"}`}
              >
                {m.trend === "down" ? "↘" : "↗"} {m.change}
              </div>
            </div>
          ))}
        </div>

        {/* Live Activity */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 border border-slate-200">
          <div className="flex flex-col md:flex-row gap-2 items-start md:items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
            <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Live Activity Stream
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {["All", "Bookings", "Payments", "System"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setActivityFilter(t)}
                  className={`px-3 py-1 rounded-md text-sm ${activityFilter === t ? "bg-primary-color text-white" : "bg-slate-100 dark:bg-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-500"}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div className="divide-y divide-slate-200 dark:divide-slate-700">
            {visibleActivities.length === 0 ? (
              <div className="p-4 text-sm text-slate-500 dark:text-slate-400">
                {activityFilter === "Payments"
                  ? "Keine Zahlungsdaten verfügbar."
                  : "Keine aktuellen Aktivitäten."}
              </div>
            ) : (
              visibleActivities.map((a, i) => (
                <div
                  key={i}
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4"
                >
                  <div className="flex flex-col sm:flex-row items-start sm:items-center sm:gap-3 gap-2">
                    <div className="text-xl" aria-hidden>
                      {a.icon}
                    </div>
                    <div>
                      <div className="font-medium text-slate-900 dark:text-slate-100">
                        {a.title}
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">
                        {a.details}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    {timeAgo(a.ts)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Charts grid (placeholders) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 border border-slate-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-0 justify-between p-4 border-b border-slate-200 dark:border-slate-700">
              <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Live Booking Trends
              </div>
              <div className="flex items-center gap-2">
                {["1H", "4H", "24H"].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTrendRange(t)}
                    className={`px-3 py-1 rounded-md text-sm ${trendRange === t ? "bg-primary-color text-white" : "bg-slate-100 dark:bg-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-500"}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-64 p-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={trendData}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} width={50} />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 12 }}
                    width={50}
                  />
                  <Tooltip
                    formatter={(value, name) => [
                      value,
                      name === "bookings" ? "Bookings" : "Revenue (€)",
                    ]}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="bookings"
                    name="Bookings"
                    yAxisId="left"
                    stroke="#4A90E2"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    name="Revenue (€)"
                    yAxisId="right"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 border border-slate-200">
            <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
              <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                LLM Performance
              </div>
            </div>
            <div className="h-64 p-2">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={llmPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    labelLine={false}
                    label={({ name, value }) =>
                      `${name} ${Number(value).toFixed(1)}%`
                    }
                  >
                    {llmPieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={index === 0 ? "#10b981" : "#ef4444"}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [
                      `${Number(value).toFixed(1)}%`,
                      name,
                    ]}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* System Health */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 border border-slate-200">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-0 justify-between p-4 border-b border-slate-200 dark:border-slate-700">
            <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              System Health Status
            </div>
            <div>
              <button className="px-3 py-1 rounded-md text-sm bg-slate-100 dark:bg-slate-600 text-slate-700 dark:text-slate-200">
                Auto-refresh: ON
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 p-4">
            {health.map((h) => (
              <div
                key={h.label}
                className="text-center border border-slate-200 dark:border-slate-600 rounded-lg sm:p-4 p-3 dark:bg-slate-700/30"
              >
                <div
                  className={`mx-auto w-12 h-12 flex items-center justify-center rounded-full font-bold ${h.intent === "excellent" ? "bg-green-100 dark:bg-green-900/40 text-slate-900 dark:text-green-200" : h.intent === "good" ? "bg-blue-100 dark:bg-blue-900/40 text-slate-900 dark:text-blue-200" : "bg-yellow-100 dark:bg-yellow-900/40 text-slate-900 dark:text-yellow-200"}`}
                >
                  {h.score}
                </div>
                <div className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                  {h.label}
                </div>
                <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {h.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
